import { useCallback, useEffect, useRef, useState } from 'react'
import { json, request } from '../api'
import { useGetToken } from '../lib/auth'

// This PC-only inherited operator panel follows the existing Korean admin UI.
// Customer-facing manufacturing copy is localized in the mobile panel (ko/en/ja/zh).

interface Publication {
  id: string
  version: number
  status: string
  proposed_quantity: number | string
  unit: string
  customer_amount: number | string
  currency: string
  approved_at?: string
}
interface AvailableOffer {
  id: string
  factory_name: string
  proposed_quantity: number | string
  unit: string
  amount?: number | string
  currency: string
  status: string
}
interface Workspace {
  enabled: boolean
  local_only?: boolean
  request_id?: string
  requests?: Array<{ quantity?: string | number; unit?: string }>
  publications?: Publication[]
  quotes?: Publication[]
  available_offers?: AvailableOffer[]
}

const PUBLICATION_STATUS: Record<string, string> = {
  published: '고객 검토 대기',
  approved: '고객 승인 접수',
  superseded: '새 견적으로 대체됨',
  expired: '기한 만료',
  revoked: '게시 철회',
}

/** Uses the existing admin identity. It never opens the mail ledger or sends messages. */
export function ManufacturingPanel({
  quoteId,
  onEnabled,
}: {
  quoteId: string
  onEnabled?: (enabled: boolean) => void
}) {
  const getToken = useGetToken()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [offerId, setOfferId] = useState('')
  const [amount, setAmount] = useState('')
  const [terms, setTerms] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const pending = useRef<{ fingerprint: string; operationId: string } | null>(null)
  const readSequence = useRef(0)
  const activeId = useRef(quoteId)
  useEffect(() => {
    activeId.current = quoteId
    return () => {
      activeId.current = ''
      readSequence.current += 1
    }
  }, [quoteId])
  const path = `/admin/quotes/${encodeURIComponent(quoteId)}/manufacturing`
  const reload = useCallback(async () => {
    const sequence = ++readSequence.current
    try {
      const next = await request<Workspace>(getToken, path)
      if (activeId.current !== quoteId || sequence !== readSequence.current) return
      setWorkspace(next)
      onEnabled?.(next.enabled)
      setError('')
    } catch {
      if (activeId.current === quoteId && sequence === readSequence.current)
        setError('제조 연동 정보를 불러오지 못했습니다. 연결 상태를 확인하고 다시 조회하세요.')
    }
  }, [getToken, path, quoteId, onEnabled])
  useEffect(() => {
    if (busy) return
    void reload()
    const interval = setInterval(() => {
      if (!busy && document.visibilityState === 'visible') void reload()
    }, 30_000)
    return () => clearInterval(interval)
  }, [reload, busy])
  const publish = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    const customerAmount = Number(amount)
    if (
      !offerId ||
      !amount.trim() ||
      !Number.isSafeInteger(customerAmount) ||
      customerAmount <= 0 ||
      !terms.trim() ||
      !validUntil
    ) {
      setError('공장 제안, 고객 총액, 조건, 유효 기한을 모두 입력하세요.')
      return
    }
    const body = {
      offer_id: offerId,
      customer_amount: customerAmount,
      customer_terms: terms.trim(),
      valid_until: new Date(`${validUntil}T23:59:59`).toISOString(),
    }
    const fingerprint = JSON.stringify({ quoteId, ...body })
    const operationId =
      pending.current?.fingerprint === fingerprint
        ? pending.current.operationId
        : crypto.randomUUID()
    pending.current = { fingerprint, operationId }
    readSequence.current += 1
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await request(
        getToken,
        `${path}/publish`,
        json('POST', { operation_id: operationId, ...body }),
      )
      pending.current = null
      if (activeId.current !== quoteId) return
      setNotice('고객 앱에 견적을 게시했습니다. 고객 승인은 별도로 기다립니다.')
      await reload()
    } catch {
      if (activeId.current === quoteId)
        setError(
          '게시 결과를 확인하지 못했습니다. 새로고침으로 상태를 확인하세요. 같은 내용으로 재시도하면 중복 게시를 방지합니다.',
        )
    } finally {
      setBusy(false)
    }
  }
  if (!workspace?.enabled) return null
  if (workspace.local_only === false)
    return (
      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="font-semibold">제조 협력 진행 중</h2>
        <p>제조 견적과 고객 승인·발주·정산은 공장 관리에서 처리합니다.</p>
        <a
          className="underline"
          href={`/manufacturing${workspace.request_id ? `?request=${encodeURIComponent(workspace.request_id)}` : ''}`}
        >
          이 요청의 공장 관리 열기
        </a>
      </section>
    )
  const offers = workspace?.available_offers ?? []
  const publications = workspace?.publications ?? workspace?.quotes ?? []
  const selectedOffer = offers.find((offer) => offer.id === offerId)
  return (
    <section
      className="space-y-4 rounded-lg border border-orange-200 bg-orange-50 p-4"
      aria-label="제조 요청 연결"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">제조 요청 · 고객 승인</h2>
        <button type="button" disabled={busy} onClick={() => void reload()} className="underline">
          새로고침
        </button>
      </div>
      <p className="text-sm">
        이 요청에 연결된 공장 제안만 확인합니다. 고객에게 보여줄 총액과 조건을 확정해 게시하세요.
        영업메일 발송 목록과 별도이며 메일·결제를 실행하지 않습니다.
      </p>
      {workspace?.request_id && <p className="text-xs">제조 요청 번호: {workspace.request_id}</p>}
      {workspace?.requests?.[0]?.quantity && (
        <p className="text-sm">
          고객 요청 수량: {workspace.requests[0].quantity} {workspace.requests[0].unit}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-green-800">
          {notice}
        </p>
      )}
      {publications.map((p) => (
        <div key={`${p.id}:${p.version}`} className="rounded border bg-white p-3 text-sm">
          <strong>
            {p.proposed_quantity} {p.unit} · {p.currency}{' '}
            {Number(p.customer_amount).toLocaleString()}
          </strong>
          <p>
            견적 {p.version}판 ·{' '}
            {p.approved_at ? '고객 승인 접수' : (PUBLICATION_STATUS[p.status] ?? p.status)}
          </p>
          {p.approved_at && <p>승인 시각: {p.approved_at} · 발주 조건 확인 필요</p>}
        </div>
      ))}
      {offers.length === 0 ? (
        <p className="text-sm">
          게시 가능한 공장 제안이 없습니다. 로컬 공장 관리자에서 요청을 배정하고 공장 답변을
          검토하세요.
        </p>
      ) : (
        <form onSubmit={(event) => void publish(event)} className="grid gap-3 text-sm">
          <label>
            공장 제안
            <select
              value={offerId}
              onChange={(event) => setOfferId(event.target.value)}
              disabled={busy}
              required
              className="mt-1 w-full rounded border bg-white p-2"
            >
              <option value="">선택하세요</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.factory_name} · {o.proposed_quantity} {o.unit} · {o.status}
                </option>
              ))}
            </select>
          </label>
          <label>
            고객 견적 총액 (VAT·배송 포함
            {selectedOffer ? ` · ${selectedOffer.currency}` : ', 제안을 먼저 선택하세요'})
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              type="number"
              min="1"
              step="1"
              disabled={busy}
              required
              className="mt-1 w-full rounded border bg-white p-2"
            />
          </label>
          <label>
            고객에게 안내할 조건
            <textarea
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
              maxLength={4000}
              disabled={busy}
              required
              className="mt-1 w-full rounded border bg-white p-2"
            />
          </label>
          <label>
            견적 유효 기한
            <input
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
              type="date"
              disabled={busy}
              required
              className="mt-1 w-full rounded border bg-white p-2"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded bg-green-800 p-3 font-semibold text-white"
          >
            검토한 조건을 고객 앱에 게시
          </button>
        </form>
      )}
    </section>
  )
}
